import {Alert, Card, openDialog, Table} from 'platform/components';
import {Box, Heading, VStack} from 'platform/foundation';

import {isEmpty, isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {BranchLeadAddressResponseBody, GetAiLeadSettingsApiResponse} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {BranchLeadAddressRow} from './BranchLeadAddressRow';
import {CreateBranchLeadAddressForm} from './CreateBranchLeadAddressForm';

// With a single active branch the default branch already gets every lead, so the card stays
// hidden until a second active branch or a first address exists.
const MIN_ACTIVE_BRANCH_COUNT = 2;

const hasLeadAddress = (
  branchLeadAddress: BranchLeadAddressResponseBody
): branchLeadAddress is BranchLeadAddressResponseBody & {emailAddress: string} =>
  isNotNil(branchLeadAddress.emailAddress);

const columns = [
  {
    element: (
      <Box padding={2}>
        <Heading color="secondary" size={5}>
          {i18n.t('entity.branch.labels.branch')}
        </Heading>
      </Box>
    ),
    id: 'branch',
    width: '40%' as const,
  },
  {
    element: (
      <Box padding={2}>
        <Heading color="secondary" size={5}>
          {i18n.t('page.settings.labels.aiLeadManagementBranchLeadAddress')}
        </Heading>
      </Box>
    ),
    id: 'emailAddress',
  },
];

interface BranchLeadAddressesCardProps extends RequiredTestIdProps {
  settings: GetAiLeadSettingsApiResponse | Nullish;
}

export function BranchLeadAddressesCard(props: BranchLeadAddressesCardProps) {
  // A backend without branch lead addresses does not send them.
  if (
    isNil(props.settings) ||
    !props.settings.enabled ||
    isNil(props.settings.branchLeadAddresses)
  ) {
    return null;
  }

  const branchLeadAddresses = props.settings.branchLeadAddresses;
  // Rows and picker options keep the API order (by branch name), so the suggested aliases follow it.
  const branchesWithAddress = branchLeadAddresses.items.filter(hasLeadAddress);
  const activeBranches = branchLeadAddresses.items.filter(
    (branchLeadAddress) => branchLeadAddress.isBranchActive
  );
  const activeBranchesWithoutAddress = activeBranches.filter((branchLeadAddress) =>
    isNil(branchLeadAddress.emailAddress)
  );
  const inactiveBranchesWithAddress = branchesWithAddress.filter(
    (branchLeadAddress) => !branchLeadAddress.isBranchActive
  );

  if (activeBranches.length < MIN_ACTIVE_BRANCH_COUNT && isEmpty(branchesWithAddress)) {
    return null;
  }

  const handleCreate = () =>
    openDialog(
      <CreateBranchLeadAddressForm
        branches={activeBranchesWithoutAddress}
        prefix={branchLeadAddresses.prefix}
        suffix={branchLeadAddresses.suffix}
        lengthLimit={branchLeadAddresses.lengthLimit}
        data-testid={suffixTestId('createDialog', props)}
      />,
      {
        title: i18n.t('page.settings.labels.aiLeadManagementCreateBranchLeadAddressTitle'),
      }
    );

  // An empty card is the header only: Card renders a padded body for any non-nil children.
  const addressList = isEmpty(branchesWithAddress) ? undefined : (
    <VStack spacing={4}>
      {inactiveBranchesWithAddress.map((branchLeadAddress) => (
        <Alert
          key={branchLeadAddress.branchId}
          variant="warning"
          title={i18n.t('page.settings.labels.aiLeadManagementInactiveBranchLeadAddress', {
            branch: branchLeadAddress.branchName,
          })}
          message={i18n.t(
            'page.settings.labels.aiLeadManagementInactiveBranchLeadAddressDescription',
            {address: branchLeadAddress.emailAddress}
          )}
          data-testid={suffixTestId(`inactiveBranchWarning-${branchLeadAddress.branchId}`, props)}
        />
      ))}

      <Table columns={columns} tableLayout="fixed" data-testid={suffixTestId('table', props)}>
        {branchesWithAddress.map((branchLeadAddress) => (
          <BranchLeadAddressRow
            key={branchLeadAddress.branchId}
            branchLeadAddress={branchLeadAddress}
            lengthLimit={branchLeadAddresses.lengthLimit}
            data-testid={suffixTestId(`row-${branchLeadAddress.branchId}`, props)}
          />
        ))}
      </Table>
    </VStack>
  );

  return (
    <Card
      title={i18n.t('page.settings.labels.aiLeadManagementBranchLeadAddresses')}
      subtitle={i18n.t('page.settings.labels.aiLeadManagementBranchLeadAddressesCount', {
        count: activeBranches.length,
        addressCount: activeBranches.length - activeBranchesWithoutAddress.length,
      })}
      actions={[
        {
          type: 'button',
          variant: 'secondary',
          leftIcon: 'content/add_circle',
          title: i18n.t('page.settings.actions.aiLeadManagementCreateBranchLeadAddress'),
          isDisabled: isEmpty(activeBranchesWithoutAddress),
          onClick: handleCreate,
          'data-testid': suffixTestId('create', props),
        },
      ]}
      variant="inlineWhite"
      data-testid={props['data-testid']}
    >
      {addressList}
    </Card>
  );
}
